import { useMemo } from 'react';
import { TooltipLink } from '@/components';
import { ContextLinkDefinition } from '@/core';
import { useInterpolateContextLinkFn } from '@/plugin-system/runtime/context-links';

const EMPTY_TOOLTIP_LINKS: TooltipLink[] = [];

export interface StatusHistoryCellContext {
  name: string;
  labels?: Record<string, string>;
  value: number | null;
  timestamp: number;
}

/**
 * Maps panel-scoped `ContextLinkDefinition[]` to `TooltipLink[]` for StatusHistory.
 * Cell context (name, labels, value, timestamp) is provided directly at render time
 * and captured in the `buildUrl` closure.
 */
export function useStatusHistoryTooltipLinks(
  links: ContextLinkDefinition[] | undefined,
  cellContext: StatusHistoryCellContext | undefined
): TooltipLink[] {
  const interpolateContextLink = useInterpolateContextLinkFn();

  return useMemo(() => {
    if (!links || links.length === 0) {
      return EMPTY_TOOLTIP_LINKS;
    }

    return links.map((link) => ({
      title: link.spec.title,
      buildUrl: () => {
        if (!cellContext) return link.spec.url;
        return interpolateContextLink(link.spec.url, {
          name: cellContext.name,
          labels: cellContext.labels,
          value: cellContext.value,
          timestamp: cellContext.timestamp,
        });
      },
    }));
  }, [links, cellContext, interpolateContextLink]);
}
